import { useCallback, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

/** 詳細パネルの既定幅（Tailwind の w-96 相当） */
export const DEFAULT_DETAIL_PANEL_WIDTH = 384;
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 900;

/**
 * 右端固定パネルの幅をドラッグで変更するためのフック。
 * 幅は state のみで管理し、リロードで既定値に戻る（永続化しない）。
 */
export function useResizablePanelWidth(
  initialWidth: number = DEFAULT_DETAIL_PANEL_WIDTH,
) {
  const [width, setWidth] = useState<number>(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  // ドラッグ開始時点の幅をコールバック再生成なしで参照するための ref
  const widthRef = useRef(width);
  widthRef.current = width;

  const handleResizeStart = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      // ポインタをハンドルに固定する。これによりドラッグ終了時の click が
      // オーバーレイの背景（クリックで閉じる領域）に飛ばず、パネルが閉じない
      e.currentTarget.setPointerCapture(e.pointerId);
      const startX = e.clientX;
      const startWidth = widthRef.current;
      // パネルは画面右端に固定のため、左へドラッグ（clientX 減少）で幅を広げる
      const handleMove = (moveEvent: PointerEvent) => {
        const delta = startX - moveEvent.clientX;
        // メインコンテンツ側が潰れないよう、画面幅からも上限を掛ける
        const maxByWindow = Math.max(
          MIN_PANEL_WIDTH,
          window.innerWidth - 400,
        );
        const next = Math.min(
          Math.min(MAX_PANEL_WIDTH, maxByWindow),
          Math.max(MIN_PANEL_WIDTH, startWidth + delta),
        );
        setWidth(next);
      };
      const handleUp = () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        setIsResizing(false);
      };
      // ドラッグ中はテキスト選択・カーソルのちらつきを防ぐ
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
      setIsResizing(true);
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
    },
    [],
  );

  return { width, isResizing, handleResizeStart };
}
